import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import "@/App.css";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import RegisterPage from "@/pages/RegisterPage";
import InviteRegisterPage from "@/pages/InviteRegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import DienstplanPage from "@/pages/DienstplanPage";
import SettingsPage from "@/pages/SettingsPage";
import MemberDashboardPage from "@/pages/MemberDashboardPage";
import TeamPage from "@/pages/TeamPage";
import UserSettingsPage from "@/pages/UserSettingsPage";
import LegalContentPage from "@/pages/LegalContentPage";
import { getDefaultHomePath, isPlanAdminUser } from "@/lib/roles";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, allowPlanAdmin = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowPlanAdmin && isPlanAdminUser(user)) {
    return children;
  }

  if (!allowPlanAdmin && allowedRoles?.includes("member") && isPlanAdminUser(user)) {
    return <Navigate to={getDefaultHomePath(user)} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDefaultHomePath(user)} replace />;
  }
  
  return children;
};

const ProtectedSiteAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.is_site_admin) {
    return <Navigate to={getDefaultHomePath(user)} replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={getDefaultHomePath(user)} replace />;
  }
  
  return children;
};

function AppRoutes({ location }) {
  return (
    <Routes location={location}>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/invite/:inviteCode" element={<InviteRegisterPage />} />
      <Route path="/datenschutz" element={<LegalContentPage slug="datenschutz" heading="Datenschutz" />} />
      <Route path="/impressum" element={<LegalContentPage slug="impressum" heading="Impressum" />} />
      
      {/* Owner Routes */}
      <Route path="/dienstplan" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={<Navigate to="/dienstplan" replace />} />
      <Route path="/dienstplan/:id" element={
        <ProtectedRoute allowPlanAdmin>
          <DienstplanPage mode="dashboard" />
        </ProtectedRoute>
      } />
      <Route path="/dienstplan/:id/veranstaltungen" element={
        <ProtectedRoute allowPlanAdmin>
          <DienstplanPage mode="veranstaltungen" />
        </ProtectedRoute>
      } />
      <Route path="/dienstplan/:id/einstellungen" element={
        <ProtectedRoute allowPlanAdmin>
          <DienstplanPage mode="einstellungen" />
        </ProtectedRoute>
      } />
      <Route path="/dienstplan/:id/personal" element={
        <ProtectedRoute allowPlanAdmin>
          <TeamPage />
        </ProtectedRoute>
      } />
      <Route path="/dienstplan/:id/angestellte" element={<Navigate to="../personal" replace />} />
      <Route path="/site-admin/email" element={
        <ProtectedSiteAdminRoute>
          <SettingsPage />
        </ProtectedSiteAdminRoute>
      } />
      <Route path="/settings" element={<Navigate to="/site-admin/email" replace />} />
      <Route path="/profil" element={
        <ProtectedRoute>
          <UserSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/team/:id" element={
        <ProtectedRoute allowPlanAdmin>
          <TeamPage />
        </ProtectedRoute>
      } />
      
      {/* Member Routes */}
      <Route path="/member" element={
        <ProtectedRoute allowedRoles={["member"]}>
          <MemberDashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RouteTransition() {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("route-fade-in");

  const currentKey = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.pathname, location.search, location.hash]
  );
  const displayKey = useMemo(
    () => `${displayLocation.pathname}${displayLocation.search}${displayLocation.hash}`,
    [displayLocation.pathname, displayLocation.search, displayLocation.hash]
  );

  useEffect(() => {
    if (currentKey !== displayKey) {
      setTransitionStage("route-fade-out");
    }
  }, [currentKey, displayKey]);

  useEffect(() => {
    if (transitionStage !== "route-fade-out") return;

    const timeoutId = setTimeout(() => {
      setDisplayLocation(location);
      setTransitionStage("route-fade-in");
    }, 120);

    return () => clearTimeout(timeoutId);
  }, [transitionStage, location]);

  return (
    <div className={`route-transition ${transitionStage}`}>
      <AppRoutes location={displayLocation} />
    </div>
  );
}

function ApiRequestActivity() {
  const [activeRequests, setActiveRequests] = useState(0);
  const [lastMethod, setLastMethod] = useState("get");
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [dbDisconnected, setDbDisconnected] = useState(false);

  const isDatabaseConnectivityError = (error) => {
    const status = error?.response?.status;
    const detail =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      "";
    const normalized = String(detail).toLowerCase();

    if (error?.response?.data?.error_code === "DB_UNAVAILABLE") {
      return true;
    }

    if (status === 503) {
      return true;
    }

    if (status >= 500) {
      return /(database|datenbank|sql|odbc|connection|aspnetusers|smartdienstplan)/i.test(normalized);
    }

    return false;
  };

  useEffect(() => {
    const increment = (method = "get") => {
      setLastMethod(String(method).toLowerCase());
      setActiveRequests((prev) => prev + 1);
    };

    const decrement = () => {
      setActiveRequests((prev) => Math.max(0, prev - 1));
    };

    const requestInterceptorId = axios.interceptors.request.use(
      (config) => {
        increment(config?.method);
        return config;
      },
      (error) => {
        decrement();
        return Promise.reject(error);
      }
    );

    const responseInterceptorId = axios.interceptors.response.use(
      (response) => {
        decrement();
        setDbDisconnected(false);
        return response;
      },
      (error) => {
        decrement();
        if (isDatabaseConnectivityError(error)) {
          setDbDisconnected(true);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, []);

  useEffect(() => {
    if (activeRequests > 0) {
      setIsLeaving(false);
      setIsVisible(true);
      return;
    }

    if (!isVisible) return;

    setIsLeaving(true);
    const timeoutId = setTimeout(() => {
      setIsVisible(false);
      setIsLeaving(false);
    }, 180);

    return () => clearTimeout(timeoutId);
  }, [activeRequests, isVisible]);

  const isSaving = ["post", "put", "patch", "delete"].includes(lastMethod);

  return (
    <>
      {dbDisconnected ? (
        <div
          className="fixed top-0 left-0 right-0 z-[120] bg-red-600 text-white px-4 py-2 text-sm text-center font-medium shadow-md"
          role="alert"
          aria-live="assertive"
          data-testid="db-connection-warning"
        >
          Die API hat aktuell keine Verbindung zur Datenbank. Einige Funktionen sind nicht verfuegbar.
        </div>
      ) : null}
      {isVisible ? (
        <>
          <div className={`api-progress ${isLeaving ? "api-progress-out" : ""}`} aria-hidden="true" />
          <div
            className={`api-activity ${isLeaving ? "api-activity-out" : "api-activity-in"}`}
            role="status"
            aria-live="polite"
          >
            <span className="api-activity-spinner" aria-hidden="true" />
            <span>{isSaving ? "Lade..." : "Lade..."}</span>
          </div>
        </>
      ) : null}
    </>
  );
}

function App() {
  const [bootLoading, setBootLoading] = useState(true);

  useEffect(() => {
    const finishLoading = () => {
      setBootLoading(false);
    };

    if (document.readyState === "complete") {
      finishLoading();
      return undefined;
    }

    window.addEventListener("load", finishLoading);
    return () => window.removeEventListener("load", finishLoading);
  }, []);

  if (bootLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#17c671]/20 border-t-[#17c671]" />
          <p className="text-sm text-gray-600">Seite wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <ApiRequestActivity />
        <RouteTransition />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
